/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import { AccountModel, AccountsDict, ConnectedAccountSubtype } from 'src/types/account.model';
import { FullUserModel } from 'src/types/user.model';
import BWDataService from './bw-data.service';
import CashFlowService from 'src/services/cashflow.service';
import { LocalDate } from '@js-joda/core';
import { AccountType } from 'src/types/account-type.enum';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import { DebtModel } from 'src/types/debt.model';
import { AccountSyncResult, AccountSyncStatusModel } from 'src/types/account-sync-status.model';

export default class AccountService extends BWDataService {
    /** Get the cash and banking connected account subtypes in an array */
    public static cashAndBankingConnectedAccountSubtypes: ConnectedAccountSubtype[] = [
        ConnectedAccountSubtype.Checking,
        ConnectedAccountSubtype.Savings
    ];

    /** Get the investments and retirement connected account subtypes in an array */
    public static investmentsAndRetirementConnectedAccountSubtypes: ConnectedAccountSubtype[] = [
        ConnectedAccountSubtype._529,
        ConnectedAccountSubtype.CD,
        ConnectedAccountSubtype.PrivateStock,
        ConnectedAccountSubtype._401K,
        ConnectedAccountSubtype._403B,
        ConnectedAccountSubtype.IRA,
        ConnectedAccountSubtype.Pension
    ];

    /** Get the loan and debt connected account subtypes in an array */
    public static loanAndDebtConnectedAccountSubtypes: ConnectedAccountSubtype[] = [
        ConnectedAccountSubtype.Mortgage,
        ConnectedAccountSubtype.HomeEquityLoan,
        ConnectedAccountSubtype.StudentLoan,
        ConnectedAccountSubtype.AutoLoan
    ];

    public static async getAccounts(): Promise<AccountModel[]> {
        const accounts: AccountModel[] = await super.get('/accounts');
        for (let account of accounts) {
            account = AccountModel.deserializeDates(account);
        }
        return accounts;
    }

    public static async updateAccount(account: AccountModel): Promise<void> {
        await this.put(`/accounts/${account.id}`, {
            name: account.name,
            current: account.current
        });
    }

    public static async hideAccount(id: number): Promise<number> {
        return await this.delete(`/accounts/${id}`);
    }

    public static async showAccount(accountId: string): Promise<number> {
        return await this.delete('/accounts_hidden/' + encodeURIComponent(accountId));
    }

    public static async hiddenAccounts(): Promise<AccountModel[]> {
        const accounts: AccountModel[] = await this.get('/accounts_hidden/');
        for (const account of accounts) {
            AccountModel.deserializeDates(account);
        }

        return accounts;
    }

    public static getAverageExpenses(user: FullUserModel): number {
        const offset = CashFlowService.getCashFlowIndexWithDate(user.cashFlow, LocalDate.now());
        return Math.abs(user.cashFlow[offset].expense) / 12;
    }

    public static async getExpenseAccountsDict(user: FullUserModel): Promise<AccountsDict> {
        const [visibleAccounts, hiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const accountsDict: AccountsDict = {};
        for (const account of visibleAccounts)
            if (account.type === AccountType.CREDIT) accountsDict[account.accountId] = { account, hidden: false };
        for (const account of hiddenAccounts)
            if (account.type === AccountType.CREDIT) accountsDict[account.accountId] = { account, hidden: true };

        return accountsDict;
    }

    public static async getNonExpenseAccountsDict(user: FullUserModel): Promise<AccountsDict> {
        const [visibleAccounts, hiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);
        const nonExpenseAccountsDict: AccountsDict = {};

        for (const account of visibleAccounts)
            if (account.type !== AccountType.CREDIT)
                nonExpenseAccountsDict[account.accountId] = { account, hidden: false };
        for (const account of hiddenAccounts)
            if (account.type !== AccountType.CREDIT)
                nonExpenseAccountsDict[account.accountId] = { account, hidden: true };

        return nonExpenseAccountsDict;
    }

    /* TODO: the filters below to get cash and banking, investments and retirement, and loan and debts connected
         accounts, should ideally be in the backend. */

    /** Retrieve all your cash and banking connected accounts */
    public static async getCashAndBankingConnectedAccounts(
        user: FullUserModel
    ): Promise<[AccountModel[], AccountModel[]]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const cashAndBankingVisibleConnectedAccounts: AccountModel[] = [];
        const cashAndBankingHiddenConnectedAccounts: AccountModel[] = [];
        const cashAndBankingAccountSubtypes: string[] = Object.values(
            AccountService.cashAndBankingConnectedAccountSubtypes
        );

        for (const account of newVisibleAccounts) {
            if (account.type === AccountType.DEPOSITORY && cashAndBankingAccountSubtypes.includes(account.subtype)) {
                cashAndBankingVisibleConnectedAccounts.push(account);
            }
        }

        for (const account of newHiddenAccounts) {
            if (account.type === AccountType.DEPOSITORY && cashAndBankingAccountSubtypes.includes(account.subtype)) {
                cashAndBankingHiddenConnectedAccounts.push(account);
            }
        }

        return [cashAndBankingVisibleConnectedAccounts, cashAndBankingHiddenConnectedAccounts];
    }

    /** Retrieve all your non cash and banking connected accounts */
    public static async getNonCashAndBankingConnectedAccounts(user: FullUserModel): Promise<AccountModel[]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const allAccounts: AccountModel[] = newVisibleAccounts.concat(newHiddenAccounts);
        const nonCashAndBankingConnectedAccounts: AccountModel[] = [];
        const cashAndBankingAccountSubtypes: string[] = Object.values(
            AccountService.cashAndBankingConnectedAccountSubtypes
        );

        for (const account of allAccounts) {
            if (account.type !== AccountType.DEPOSITORY || !cashAndBankingAccountSubtypes.includes(account.subtype)) {
                nonCashAndBankingConnectedAccounts.push(account);
            }
        }

        return nonCashAndBankingConnectedAccounts;
    }

    /** Retrieve all your investment and retirement connected accounts */
    public static async getInvestmentsAndRetirementConnectedAccounts(
        user: FullUserModel
    ): Promise<[AccountModel[], AccountModel[]]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const investmentsAndRetirementsVisibleConnectedAccounts: AccountModel[] = [];
        const investmentsAndRetirementsHiddenConnectedAccounts: AccountModel[] = [];
        const investmentsAndRetirementsAccountSubtypes: string[] = Object.values(
            AccountService.investmentsAndRetirementConnectedAccountSubtypes
        );

        for (const account of newVisibleAccounts) {
            if (
                account.type === AccountType.INVESTMENT &&
                investmentsAndRetirementsAccountSubtypes.includes(account.subtype)
            ) {
                investmentsAndRetirementsVisibleConnectedAccounts.push(account);
            } else if (account.type === AccountType.DEPOSITORY && account.subtype === ConnectedAccountSubtype.CD) {
                investmentsAndRetirementsVisibleConnectedAccounts.push(account);
            }
        }

        for (const account of newHiddenAccounts) {
            if (
                account.type === AccountType.INVESTMENT &&
                investmentsAndRetirementsAccountSubtypes.includes(account.subtype)
            ) {
                investmentsAndRetirementsHiddenConnectedAccounts.push(account);
            } else if (account.type === AccountType.DEPOSITORY && account.subtype === ConnectedAccountSubtype.CD) {
                investmentsAndRetirementsHiddenConnectedAccounts.push(account);
            }
        }

        return [investmentsAndRetirementsVisibleConnectedAccounts, investmentsAndRetirementsHiddenConnectedAccounts];
    }

    /** Retrieve all your non investment and retirement connected accounts */
    public static async getNonInvestmentsAndRetirementConnectedAccounts(user: FullUserModel): Promise<AccountModel[]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const allAccounts: AccountModel[] = newVisibleAccounts.concat(newHiddenAccounts);
        const nonInvestmentsAndRetirementsConnectedAccounts: AccountModel[] = [];
        const investmentsAndRetirementsAccountSubtypes: string[] = Object.values(
            AccountService.investmentsAndRetirementConnectedAccountSubtypes
        );

        for (const account of allAccounts) {
            if (
                (account.type !== AccountType.INVESTMENT ||
                    !investmentsAndRetirementsAccountSubtypes.includes(account.subtype)) &&
                (account.type !== AccountType.DEPOSITORY || account.subtype !== ConnectedAccountSubtype.CD)
            ) {
                nonInvestmentsAndRetirementsConnectedAccounts.push(account);
            }
        }

        return nonInvestmentsAndRetirementsConnectedAccounts;
    }

    /** Retrieve all your loan and debt connected accounts */
    public static async getLoanAndDebtConnectedAccounts(
        user: FullUserModel
    ): Promise<[AccountModel[], AccountModel[]]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const loanAndDebtVisibleConnectedAccounts: AccountModel[] = [];
        const loanAndDebtHiddenConnectedAccounts: AccountModel[] = [];
        const loanAndDebtAccountSubtypes: string[] = Object.values(AccountService.loanAndDebtConnectedAccountSubtypes);

        for (const account of newVisibleAccounts) {
            if (account.type === AccountType.LOAN && loanAndDebtAccountSubtypes.includes(account.subtype)) {
                loanAndDebtVisibleConnectedAccounts.push(account);
            }
        }

        for (const account of newHiddenAccounts) {
            if (account.type === AccountType.LOAN && loanAndDebtAccountSubtypes.includes(account.subtype)) {
                loanAndDebtHiddenConnectedAccounts.push(account);
            }
        }

        return [loanAndDebtVisibleConnectedAccounts, loanAndDebtHiddenConnectedAccounts];
    }

    /** Retrieves all your non loan and debt connected accounts */
    public static async getNonLoanAndDebtConnectedAccounts(user: FullUserModel): Promise<AccountModel[]> {
        const [newVisibleAccounts, newHiddenAccounts] = await Promise.all([
            AccountService.getAccounts(),
            AccountService.hiddenAccounts()
        ]);

        const allAccounts: AccountModel[] = newVisibleAccounts.concat(newHiddenAccounts);
        const nonLoanAndDebtConnectedAccounts: AccountModel[] = [];
        const loanAndDebtAccountSubtypes: string[] = Object.values(AccountService.loanAndDebtConnectedAccountSubtypes);

        for (const account of allAccounts) {
            if (account.type !== AccountType.LOAN || !loanAndDebtAccountSubtypes.includes(account.subtype)) {
                nonLoanAndDebtConnectedAccounts.push(account);
            }
        }

        return nonLoanAndDebtConnectedAccounts;
    }

    /** Get total for all accounts */
    public static getTotal(
        visibleAccounts: AccountModel[],
        hiddenAccounts: AccountModel[],
        investmentHoldings: InvestmentHoldingModel[],
        debts: DebtModel[]
    ): number {
        const total =
            _.sumBy([...visibleAccounts, ...hiddenAccounts], (e) => e.current) +
            _.sumBy([...investmentHoldings, ...debts], (e) => e.amount);
        return total;
    }

    public static getSavings(user: FullUserModel): number {
        let savings: number = 0;

        for (const account of user.accounts) {
            switch (account.type) {
                case 'depository':
                    savings += account.current;
                    break;
            }
        }

        return savings;
    }

    public static getAmexCards(user: FullUserModel): AccountModel[] {
        return _.chain(user.accounts)
            .filter({ subtype: 'credit card' })
            .filter((account: AccountModel) => account.institution.name.includes('American Express'))
            .value();
    }

    public static getSavingsAccounts(user: FullUserModel): AccountModel[] {
        return _.filter(user.accounts, { type: 'depository' });
    }

    public static async getAccountSyncStatuses(): Promise<AccountSyncStatusModel[]> {
        return await this.get('/accounts/sync-status');
    }

    public static async resyncAccount(id: number): Promise<void> {
        return await this.post(`/accounts/sync-transactions/${id}`, {});
    }

    public static isSyncingAccounts(statuses: AccountSyncStatusModel[]): boolean {
        if (!statuses) return false;
        const currentlySyncing = _.filter(statuses, { syncResult: AccountSyncResult.SYNCING });
        return currentlySyncing.length > 0;
    }

    public static isSyncingAccount(accountId: string, statuses: AccountSyncStatusModel[]): boolean {
        if (!accountId || !statuses) return false;
        const syncingAccount = _.find(statuses, { accountId, syncResult: AccountSyncResult.SYNCING });
        return !!syncingAccount;
    }

    public static hasSameInstitutionAsSyncingAccount(
        id: number,
        accounts: AccountModel[],
        statuses: AccountSyncStatusModel[]
    ): boolean {
        if (!id || !accounts || !statuses) return false;
        const syncingAccountIds: string[] = _.chain(statuses)
            .filter({ syncResult: AccountSyncResult.SYNCING })
            .map('accountId')
            .value();
        const syncingAccountInstitutionIds: number[] = _.chain(accounts)
            .filter((account) => _.includes(syncingAccountIds, account.accountId))
            .map((account) => account.institutionId)
            .value();
        return syncingAccountInstitutionIds?.includes(id);
    }

    public static hasSyncError(statuses: AccountSyncStatusModel[]): boolean {
        if (!statuses) return false;
        const syncErrors = _.filter(statuses, (status: AccountSyncStatusModel) => {
            return (
                status.syncResult === AccountSyncResult.ERROR_PERMANENT ||
                status.syncResult === AccountSyncResult.ERROR_PHANT_INTERNAL ||
                status.syncResult === AccountSyncResult.ERROR_RESYNC_NEEDED ||
                status.syncResult === AccountSyncResult.ERROR_PLAID_RELINK_NEEDED
            );
        });
        return syncErrors.length > 0;
    }

    public static getAccountSyncError(accountId: string, statuses: AccountSyncStatusModel[]): AccountSyncResult {
        if (!accountId || !statuses) return null;
        const accountStatus: AccountSyncStatusModel = _.find(statuses, (status) => status.accountId === accountId);
        const hasSyncError: boolean =
            accountStatus?.syncResult === AccountSyncResult.ERROR_PLAID_RELINK_NEEDED ||
            accountStatus?.syncResult === AccountSyncResult.ERROR_RESYNC_NEEDED ||
            accountStatus?.syncResult === AccountSyncResult.ERROR_PERMANENT ||
            accountStatus?.syncResult === AccountSyncResult.ERROR_PHANT_INTERNAL;
        if (hasSyncError) return accountStatus?.syncResult;
        return null;
    }

    /** Gets formatted display name depending on account's name, officialName, mask, and institution */
    public static getDisplayName(account: AccountModel): string {
        if (!account) return '';

        if (account.institution?.name === 'American Express') return account.officialName;

        if (account.mask && account.officialName) return `${account.officialName} (-${account.mask})`;

        if (account.officialName) return account.officialName;

        return account.name;
    }
}
